import { LoadingManager } from 'three';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';

export function createLoadingManager({ onProgress, onLoad, onError } = {}) {
  const manager = new LoadingManager();

  manager.onProgress = (url, loaded, total) => {
    const percentage = total > 0 ? Math.round((loaded / total) * 100) : 0;
    onProgress?.({ url, loaded, total, percentage });
  };
  manager.onLoad = () => onLoad?.();
  manager.onError = (url) => onError?.(url);

  return manager;
}

export function loadGLTF(
  url,
  {
    manager,
    dracoPath = '/draco/',
    ktx2Path = '/basis/',
    renderer,
  } = {},
) {
  const loader = new GLTFLoader(manager);
  const dracoLoader = new DRACOLoader(manager);
  dracoLoader.setDecoderPath(dracoPath);
  loader.setDRACOLoader(dracoLoader);

  let ktx2Loader = null;
  if (renderer) {
    ktx2Loader = new KTX2Loader(manager);
    ktx2Loader.setTranscoderPath(ktx2Path);
    ktx2Loader.detectSupport(renderer);
    loader.setKTX2Loader(ktx2Loader);
  }

  return new Promise((resolve, reject) => {
    loader.load(
      url,
      (gltf) => {
        dracoLoader.dispose();
        ktx2Loader?.dispose();
        resolve(gltf);
      },
      undefined,
      (error) => {
        dracoLoader.dispose();
        ktx2Loader?.dispose();
        reject(error);
      },
    );
  });
}

export function prepareModel(root, { castShadow = true, receiveShadow = true } = {}) {
  root.traverse((object) => {
    if (!object.isMesh) return;
    object.castShadow = castShadow;
    object.receiveShadow = receiveShadow;
    object.frustumCulled = true;

    const materials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of materials) {
      for (const key of ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap']) {
        const texture = material?.[key];
        if (texture) texture.anisotropy = Math.min(texture.anisotropy || 1, 4);
      }
    }
  });

  return root;
}
